import { useEffect, useState } from 'react'
import { Alert, Button, Card, Modal, Select, Space, Table, Tag, Typography, message } from 'antd'
import Popconfirm from '../components/Popconfirm'
import { MailOutlined } from '@ant-design/icons'
import * as api from '../lib/api'
import { useI18n } from '../i18n/I18nProvider'
import Title from 'antd/es/skeleton/Title'

const { Text } = Typography

function managerName(manager) {
  return [manager.first_name, manager.last_name].filter(Boolean).join(' ') || manager.email
}

export default function ManagerDigestExclusions() {
  const { t } = useI18n()
  const [messageApi, messageContextHolder] = message.useMessage()
  const [exclusions, setExclusions] = useState([])
  const [loading, setLoading] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [candidateSearch, setCandidateSearch] = useState('')
  const [candidates, setCandidates] = useState([])
  const [candidateLoading, setCandidateLoading] = useState(false)
  const [selectedManagerId, setSelectedManagerId] = useState()
  const [saving, setSaving] = useState(false)
  const [removing, setRemoving] = useState({})

  async function loadExclusions() {
    setLoading(true)
    try {
      setExclusions(await api.getManagerDigestExclusions())
    } catch (error) {
      messageApi.error(error.message || t('digest_exclusions.messages.load_failed', 'Unable to load email exclusions'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadExclusions()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!modalOpen) return undefined
    let canceled = false
    const timer = window.setTimeout(async () => {
      setCandidateLoading(true)
      try {
        const results = await api.getManagerDigestExclusionCandidates(candidateSearch.trim())
        if (!canceled) setCandidates(results)
      } catch (error) {
        if (!canceled) messageApi.error(error.message || t('digest_exclusions.messages.search_approvers_failed', 'Unable to search Approvers'))
      } finally {
        if (!canceled) setCandidateLoading(false)
      }
    }, 300)
    return () => {
      canceled = true
      window.clearTimeout(timer)
    }
  }, [candidateSearch, modalOpen, t])

  function openModal() {
    setCandidateSearch('')
    setCandidates([])
    setSelectedManagerId(undefined)
    setModalOpen(true)
  }

  async function addExclusion() {
    if (!selectedManagerId) return
    setSaving(true)
    try {
      await api.createManagerDigestExclusion(selectedManagerId)
      messageApi.success(t('digest_exclusions.messages.approver_disabled', 'Daily digest email disabled for this Approver'))
      setModalOpen(false)
      await loadExclusions()
    } catch (error) {
      messageApi.error(error.message || t('digest_exclusions.messages.disable_failed', 'Unable to disable daily digest email'))
    } finally {
      setSaving(false)
    }
  }

  async function removeExclusion(managerId) {
    setRemoving(previous => ({ ...previous, [managerId]: true }))
    try {
      await api.deleteManagerDigestExclusion(managerId)
      messageApi.success(t('digest_exclusions.messages.approver_enabled', 'Daily digest email enabled for this Approver'))
      await loadExclusions()
    } catch (error) {
      messageApi.error(error.message || t('digest_exclusions.messages.enable_failed', 'Unable to enable daily digest email'))
    } finally {
      setRemoving(previous => ({ ...previous, [managerId]: false }))
    }
  }

  const columns = [
    {
      title: t('common.name', 'Name'),
      key: 'name',
      render: (_, record) => managerName(record)
    },
    { title: t('common.email', 'Email'), dataIndex: 'email' },
    { title: t('common.position', 'Position'), dataIndex: 'position', render: value => value || '—' },
    {
      title: t('digest_exclusions.status', 'Email status'),
      key: 'status',
      render: () => <Tag color='default'>{t('digest_exclusions.disabled', 'Daily digest disabled')}</Tag>
    },
    {
      title: t('common.actions', 'Actions'),
      key: 'actions',
      width: 110,
      render: (_, record) => (
        <Popconfirm
          title={t('digest_exclusions.confirm_enable_approver', 'Enable daily digest email for this Approver?')}
          okText={t('common.enable', 'Enable')}
          cancelText={t('common.cancel', 'Cancel')}
          onConfirm={() => removeExclusion(record.manager_id)}
        >
          <Button size='small' loading={Boolean(removing[record.manager_id])}>
            {t('common.enable', 'Enable')}
          </Button>
        </Popconfirm>
      )
    }
  ]

  return (
    <Space orientation='vertical' size='middle' style={{ width: '100%' }}>
      {messageContextHolder}
      <Card
        size='small'
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={4} style={{ margin: 0 }}>
            {t('digest_exclusions.approver_list', 'Approver List')}
          </Title>
          <Button type='primary' onClick={openModal}>
            {t('digest_exclusions.search_approver', 'Search Approver')}
          </Button>
        </div>
        <Alert
          type='info'
          showIcon
          title={t('digest_exclusions.info.approver_title', 'Approver daily digest email exclusions')}
          description={t('digest_exclusions.info.approver_description', 'Approvers listed here will not receive the daily cross-session appraisal email digest.')}
          style={{ marginBottom: 16 }}
        />
        <Table
          rowKey='id'
          size='small'
          loading={loading}
          dataSource={exclusions}
          columns={columns}
          pagination={false}
          locale={{ emptyText: t('digest_exclusions.empty_approvers', 'All Approvers in your HR scope can receive daily digest emails.') }}
        />
      </Card>

      <Modal
        title={t('digest_exclusions.modal.approver_title', 'Disable Approver daily digest email')}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={addExclusion}
        okText={t('digest_exclusions.modal.confirm_action', 'Confirm')}
        okButtonProps={{
          disabled: !selectedManagerId,
          className: 'digest-exclusion-confirm-button'
        }}
        confirmLoading={saving}
        destroyOnHidden
      >
        <Space orientation='vertical' size='middle' style={{ width: '100%' }}>
          <Text type='secondary'>
            {t('digest_exclusions.modal.approver_help', 'Search Approvers assigned within sessions you can access. Disabling the digest applies to that Approver across all sessions.')}
          </Text>
          <Select
            showSearch
            filterOption={false}
            value={selectedManagerId}
            onChange={setSelectedManagerId}
            onSearch={setCandidateSearch}
            searchValue={candidateSearch}
            loading={candidateLoading}
            placeholder={t('digest_exclusions.modal.search', 'Search by name, email, employee ID, or position')}
            notFoundContent={candidateLoading ? t('common.loading', 'Loading…') : t('digest_exclusions.modal.no_approvers', 'No available Approvers found')}
            options={candidates.map(manager => ({
              value: manager.id,
              label: `${managerName(manager)} — ${manager.email}`
            }))}
            suffixIcon={<MailOutlined />}
            style={{ width: '100%' }}
          />
        </Space>
      </Modal>
    </Space>
  )
}